import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, ListChecks, Layers, History, LogOut, TrendingUp, CalendarClock, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/input", label: "Input Bulanan", icon: ListChecks, testid: "nav-input" },
  { to: "/kategori", label: "Kategori & Aset", icon: Layers, testid: "nav-kategori" },
  { to: "/riwayat", label: "Riwayat & Laporan", icon: History, testid: "nav-riwayat" },
  { to: "/jadwal", label: "Jadwal Harian", icon: CalendarClock, testid: "nav-jadwal" },
  { to: "/pengaturan", label: "Pengaturan WA", icon: Settings, testid: "nav-pengaturan" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    toast.success("Berhasil keluar");
    navigate("/login");
  };

  return (
    <div className="min-h-screen grain-bg">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white/70 backdrop-blur-md">
          <div className="px-6 pt-6 pb-8 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-blue-600 text-white grid place-items-center">
              <TrendingUp className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-display font-semibold text-slate-900 leading-tight">AsetKu</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-[0.15em]">Asset Tracker</div>
            </div>
          </div>

          <nav className="flex-1 px-3 sidebar-scroll overflow-y-auto">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 mb-1 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <n.icon className="w-4 h-4" strokeWidth={1.75} />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 px-1 mb-3">
              <div className="w-9 h-9 rounded-full bg-slate-900 text-white grid place-items-center font-display font-semibold">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate" data-testid="user-name">
                  {user?.name || "Pengguna"}
                </div>
                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start gap-2 rounded-md border-slate-200 hover:bg-slate-50"
              onClick={doLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" /> Keluar
            </Button>
          </div>
        </aside>

        {/* Mobile top nav */}
        <div className="lg:hidden fixed top-0 inset-x-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-600 text-white grid place-items-center">
                <TrendingUp className="w-4 h-4" />
              </div>
              <span className="font-display font-semibold text-slate-900">AsetKu</span>
            </div>
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-button-mobile">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-1 overflow-x-auto px-2 pb-2">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `whitespace-nowrap flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
                    isActive ? "bg-blue-50 text-blue-700" : "text-slate-600"
                  }`
                }
              >
                <n.icon className="w-3.5 h-3.5" />
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="flex-1 min-w-0 pt-28 lg:pt-0">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
